import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';

import { logout } from '../../api/auth';
import { useSignedInEmail } from '../../lib/signedInAccount';
import { useSession } from '../../session';
import { ProviderKeysPanel } from '../../pages/ProviderKeys/ProviderKeysPage';
import { ConnectionsPanel } from './ConnectionsPanel';
import { DataExportPanel } from './DataExportPanel';
import { LegalPanel } from './LegalPanel';
import { LocalNodePanel } from './LocalNodePanel';
import { StoragePanel } from './StoragePanel';
import { SubscriptionsPanel } from './SubscriptionsPanel';
import { SETTINGS_SECTIONS, type SettingsSection } from './settingsSections';

function AccountPanel() {
  const { session, setSession } = useSession();
  const email = useSignedInEmail(session);
  const [, navigate] = useLocation();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function signOut() {
    setError('');
    setBusy(true);
    try {
      await logout();
      setSession(null);
      navigate('/login', { replace: true });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Sign out could not be completed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-panel-content">
      <h3>Account</h3>
      <p className="settings-account-identity">
        {email ? <>Signed in as <strong>{email}</strong></> : 'Signed in. Your email shows here after you next sign in on this device.'}
      </p>
      <p>Manage billing and account access from these existing pages.</p>
      <ul className="settings-account-links">
        <li>
          <Link to="/billing">Pay for Auteur (Billing)</Link>
          {' '}<span className="settings-account-note">Your Auteur plan and invoices. Payment links for your own
          storefront are set up in Launch, not here.</span>
        </li>
        <li><Link to="/password-reset">Password reset</Link></li>
        <li><Link to="/verify-email">Email verification</Link></li>
        <li><Link to="/settings?settings=legal">Legal: terms you accepted</Link></li>
      </ul>
      {error ? <p className="error" role="alert">{error}</p> : null}
      <button type="button" className="secondary" disabled={busy} onClick={() => void signOut()}>
        {busy ? 'Signing out…' : 'Sign out'}
      </button>
    </section>
  );
}

export interface SettingsSectionsProps {
  initialSection?: SettingsSection;
  onStorageDirtyChange?: (dirty: boolean) => void;
}

/** The settings sections the /settings page shows (the former pop-up was removed, walkthrough F-9). */
export function SettingsSections({
  initialSection = 'keys',
  onStorageDirtyChange,
}: SettingsSectionsProps) {
  const [selected, setSelected] = useState<SettingsSection>(initialSection);
  const [storageDirty, setStorageDirty] = useState(false);

  useEffect(() => {
    setSelected(initialSection);
  }, [initialSection]);

  useEffect(() => {
    onStorageDirtyChange?.(storageDirty);
  }, [onStorageDirtyChange, storageDirty]);

  function chooseSection(section: SettingsSection) {
    if (section === selected) return;
    if (storageDirty && !window.confirm('Discard your unsaved storage changes?')) return;
    setSelected(section);
  }

  let content: ReactNode;
  switch (selected) {
    case 'connections':
      content = <ConnectionsPanel onOpenSection={chooseSection} />;
      break;
    case 'keys':
      content = <ProviderKeysPanel />;
      break;
    case 'subscriptions':
      content = <SubscriptionsPanel />;
      break;
    case 'storage':
      content = <StoragePanel onDirtyChange={setStorageDirty} />;
      break;
    case 'node':
      content = <LocalNodePanel />;
      break;
    case 'data':
      content = <DataExportPanel />;
      break;
    case 'account':
      content = <AccountPanel />;
      break;
    case 'legal':
      content = <LegalPanel />;
      break;
    default:
      content = null;
  }

  return (
    <div className="settings-workspace">
      <nav className="settings-section-nav" aria-label="Settings sections">
        {SETTINGS_SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            className={selected === section.id ? 'secondary active' : 'secondary'}
            aria-current={selected === section.id ? 'page' : undefined}
            onClick={() => chooseSection(section.id)}
          >
            {section.label}
          </button>
        ))}
      </nav>
      <div className="settings-active-section">{content}</div>
    </div>
  );
}
